/* eslint-disable */
import React from 'react';
import type { City } from '../../../models/common/places';

type FormattableText = {
  text: string;
};

type PlacePrediction = {
  mainText?: FormattableText;
  placeId: string;
  secondaryText?: FormattableText;
  text: FormattableText;
  types: string[];
};

type AutocompleteSuggestion = {
  placePrediction?: PlacePrediction;
};

type AutocompleteRequest = {
  includedRegionCodes?: string[];
  input: string;
};

type PlacesLibrary = {
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions: (
      request: AutocompleteRequest,
    ) => Promise<{ suggestions: AutocompleteSuggestion[] }>;
  };
};

type GoogleMapsWithImportLibrary = typeof google.maps & {
  importLibrary?: (library: 'places') => Promise<PlacesLibrary>;
};

export const useGooglePlaces = (
  isGoogleMapsApiLoaded: boolean,
  searchTerm: string,
  countryCode?: string,
) => {
  const [places, setPlaces] = React.useState<City[]>([]);

  const fetchPlaces = async () => {
    const maps = google.maps as GoogleMapsWithImportLibrary;
    const placesLibrary =
      (await maps.importLibrary?.('places')) ?? (google.maps.places as unknown as PlacesLibrary);
    const { suggestions } = await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions(
      {
        input: searchTerm,
        ...(countryCode && { includedRegionCodes: [countryCode] }),
      },
    );

    setPlaces(parseGoogleSuggestions(suggestions));
  };

  React.useEffect(() => {
    if (!isGoogleMapsApiLoaded) {
      return;
    }

    if (!searchTerm) {
      setPlaces([]);
      return;
    }

    const timer = setTimeout(() => {
      void fetchPlaces().catch(() => {
        setPlaces([]);
      });
    }, 400);

    return () => {
      clearTimeout(timer);
    };
  }, [countryCode, isGoogleMapsApiLoaded, searchTerm]);

  return places;
};

const parseGoogleSuggestions = (suggestions: AutocompleteSuggestion[]): City[] => {
  return suggestions
    .map((suggestion) => suggestion.placePrediction)
    .filter((prediction): prediction is PlacePrediction => !!prediction)
    .filter((prediction) => !prediction.types.includes('country'))
    .map(parsePlacePrediction);
};

const parsePlacePrediction = (prediction: PlacePrediction): City => {
  const mainText = prediction.mainText?.text;
  const secondaryText = prediction.secondaryText?.text;

  if (mainText && secondaryText) {
    const secondaryTerms = secondaryText.split(',').map((term) => term.trim());
    const countryName = secondaryTerms[secondaryTerms.length - 1] ?? '';

    return {
      name: [mainText, ...secondaryTerms.slice(0, -1)].join(', '),
      countryName,
      placeId: prediction.placeId,
    };
  }

  if (mainText) {
    return {
      name: mainText,
      countryName: '',
      placeId: prediction.placeId,
    };
  }

  const terms = prediction.text.text.split(',').map((term) => term.trim());

  if (terms.length > 1) {
    return {
      name: terms.slice(0, -1).join(', '),
      countryName: terms[terms.length - 1],
      placeId: prediction.placeId,
    };
  }

  return {
    name: terms[0],
    placeId: prediction.placeId,
    countryName: '',
  };
};
